// sections/SectionLanguageCulture.jsx
import React from 'react';
import { Globe } from 'lucide-react';
import { Row, Col, Input, Select, SectionTitle, FormField, Checkbox, FileInput } from '../../../SharedInputs.jsx';
import {COUNTRY_SACC_CODES} from "../../../../CountrySACC.js";
import { useMemo } from 'react';
export function SectionLanguageCulture({ value, onChange, errors = [], dispatch }) {
  const updateField = (field, fieldValue) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const toggleAboriginalTorres = (option) => {
    dispatch({
      type: 'TOGGLE_ARRAY_ITEM',
      section: 'languageCulture',
      field: 'aboriginalOrTorres',
      value: option
    });
  };
  const updateFile = (field, file) => {
    dispatch({ type: 'SET_FILE', section: 'languageCulture', field, file });
  };
  
  const getFieldError = (fieldName) => {
    return errors.find(error => error.toLowerCase().includes(fieldName.toLowerCase()));
  };
  const countryOptions = useMemo(() => {
    return Object.entries(COUNTRY_SACC_CODES)
      .filter(([countryName]) => countryName && countryName !== "Not Specified" && countryName !== "At Sea") // optional
      .map(([countryName, saccCode]) => ({
        label: countryName.charAt(0).toUpperCase() + countryName.slice(1).toLowerCase(),
        value: saccCode,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);


  return (
    <div>
      <SectionTitle icon={Globe} step={5}>Language & Cultural Diversity</SectionTitle>
      
      <Row>
        <Col span={4}>
          <FormField label="Country of Birth"
          required
          error={getFieldError('country')}>
            <Select
                          value={value.country || ''}
                          onChange={(e) => updateField('country', e.target.value)}
                          required
                          error={getFieldError('country')}
                        >
                          <option value="">Select a country</option>
                          {countryOptions.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </Select>
          </FormField>
        </Col>
        <Col span={4}>
          <FormField label="Main language spoken at home">
            <Select value={value.languageAtHome} onChange={(e) => updateField('languageAtHome', e.target.value)}>
              <option value="English only">English only</option>
              <option value="Other">Other</option>
            </Select>
          </FormField>
        </Col>
        {value.languageAtHome === "Other" && (
          <Col span={4}>
            <FormField label="Specify other language"
            required
            error={getFieldError('other language')}>
              <Input value={value.otherLanguage} onChange={(e) => updateField('otherLanguage', e.target.value)}
              required
              error={getFieldError('other language')}
               />
            </FormField>
          </Col>
        )}
      </Row>

      <Row>
        <Col span={3}>
          <FormField label="English proficiency test" required
            error={getFieldError('english test')}>
            <Select 
              value={value.englishTest} 
              onChange={(e) => updateField('englishTest', e.target.value)}
              required
              error={getFieldError('english test')}
            >
              <option value="">None taken</option>
              <option value="Cambridge C1 Advanced">Cambridge Advanced Test (C1 Advanced)</option>
              <option value="CELPIP General">Canadian English Language Proficiency Index Program (CELPIP General)</option>
              <option value="IELTS Academic">IELTS Academic</option>
              <option value="IELTS General Training">IELTS General Training</option>
              <option value="LANGUAGECERT Academic">LANGUAGECERT Academic Test</option>
              <option value="MET">Michigan English Test (MET)</option>
              <option value="OET">Occupational English Test (OET)</option>
              <option value="PTE Academic">Pearson Test of English Academic (PTE Academic)</option>
              <option value="TOEFL iBT">Test of English as a Foreign Language internet-Based Test (TOEFL iBT)</option>
              <option value="Other">Other</option>
            </Select>
          </FormField>
        </Col>
        <Col span={3}>
          <FormField label="Overall score"
            error={getFieldError('overall score')}>
            <Input value={value.overallScore} onChange={(e) => updateField('overallScore', e.target.value)} 
              required
              error={getFieldError('overall score')}/>
          </FormField>
        </Col>
        <Col span={3}>
          <FormField label="Test date"
            error={getFieldError('test date')}>
            <Input type="date" value={value.testDate} onChange={(e) => updateField('testDate', e.target.value)} 
            required
            error={getFieldError('test date')}/>
          </FormField>
        </Col>
      </Row>
      <Row>
              <Col span={12}>
                <FormField 
                  label="Upload English proficiency test"
                  hint="If you have a current or previous English Test scores"
                  error={getFieldError('current/previous english')}
                >
                  <FileInput
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(file) => updateFile('currentEnglishCopy', file)}
                    value={value.currentEnglishCopy}
                    placeholder="Upload Test Results"
                    required
                    error={getFieldError('current/previous english')}
                  />
                </FormField>
              </Col>
        </Row>
      <Row>
        <Col>
        {/* English Proficiency Requirements*/}
        <div style={{
          marginTop: 24,
          padding: 16,
          background: '#fffbeb',
          borderRadius: 8,
          border: '1px solid #fed7aa'
        }}>
          <h4 style={{ margin: '0 0 12px', color: '#92400e' }}>CIET’s English Language Entry Requirements</h4>
          To check if you have met CIET’s English Language Entry Requirements, please refer to the Student Admission Policy and Procedure available on the CIET website (<a href='www.ciet.edu.au' target='Blank'>www.ciet.edu.au</a>)
      </div>
        </Col>
      </Row>

      <Row>
        <Col span={12}>
          <FormField label="Are you of Aboriginal or Torres Strait Islander origin?"
          hint={"For persons of both Aboriginal and Torres Strait Islander origin, mark both ‘Yes’ boxes. "}>
            <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
              <Checkbox
                checked={value.aboriginalOrTorres.includes("No")}
                onChange={() => toggleAboriginalTorres("No")}
              >
                No
              </Checkbox>
              <Checkbox
                checked={value.aboriginalOrTorres.includes("Aboriginal")}
                onChange={() => toggleAboriginalTorres("Aboriginal")}
              >
                Yes, Aboriginal
              </Checkbox>
              <Checkbox
                checked={value.aboriginalOrTorres.includes("Torres Strait Islander")}
                onChange={() => toggleAboriginalTorres("Torres Strait Islander")}
              >
                Yes, Torres Strait Islander
              </Checkbox>
            </div>
          </FormField>
        </Col>
      </Row>
    </div>
  );
}